import type { AxiosRequestConfig, AxiosResponse } from "axios";
import { camelCaseObject } from "../lib/utils"; // ensure named import
import api from "./api";

export class ApiClient {
  /**
   * GET request
   * @param url - API endpoint
   * @param requestOptions - axios request options
   */
  async get<TResponse>(
    url: string,
    requestOptions?: AxiosRequestConfig,
  ): Promise<TResponse> {
    const response = await api.get(url, requestOptions);
    const data = response.data;

    // Check if data is a non-null object and contains 'form_data'
    const isObject =
      typeof data === "object" && data !== null && !Array.isArray(data);
    const hasFormData =
      isObject && "form_data" in (data as Record<string, unknown>);

    if (isObject && hasFormData) {
      // Return original object without conversion
      return data;
    }

    // Otherwise (if it's an array or a standard object), convert to camelCase
    return camelCaseObject<TResponse>(data);
  }

  /**
   * POST request
   * @param url - API endpoint
   * @param payload - request body
   * @param requestOptions - axios request options
   */
  async post<TResponse, TPayload = void>(
    url: string,
    payload?: TPayload,
    requestOptions?: AxiosRequestConfig,
  ): Promise<TResponse> {
    const response: AxiosResponse<TResponse> = await api.post(
      url,
      payload,
      requestOptions,
    );
    const { form_data, ...rest } = response.data as Record<string, unknown>;
    const camelCased = camelCaseObject(rest);
    return (form_data ? { ...camelCased, form_data } : camelCased) as TResponse;
  }

  /**
   * PUT request
   * @param url - API endpoint
   * @param payload - request body
   * @param requestOptions - axios request options
   */
  async put<TResponse, TPayload = void>(
    url: string,
    payload?: TPayload,
    requestOptions?: AxiosRequestConfig,
  ): Promise<TResponse> {
    const response: AxiosResponse<TResponse> = await api.put(
      url,
      payload,
      requestOptions,
    );
    return camelCaseObject<TResponse>(response.data);
  }

  /**
   * PATCH request
   * @param url - API endpoint
   * @param payload - request body
   * @param requestOptions - axios request options
   */
  async patch<TResponse, TPayload = void>(
    url: string,
    payload?: TPayload,
    requestOptions?: AxiosRequestConfig,
  ): Promise<TResponse> {
    const response: AxiosResponse<TResponse> = await api.patch(
      url,
      payload,
      requestOptions,
    );
    return camelCaseObject<TResponse>(response.data);
  }

  /**
   * DELETE request
   * @param url - API endpoint
   * @param requestOptions - axios request options
   */
  async delete<TResponse>(
    url: string,
    requestOptions?: AxiosRequestConfig,
  ): Promise<TResponse> {
    const response: AxiosResponse<TResponse> = await api.delete(
      url,
      requestOptions,
    );
    return camelCaseObject<TResponse>(response.data);
  }
}

export const apiClient = new ApiClient();
